import type { LinksFunction, MetaFunction } from "@remix-run/cloudflare";
import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
} from "@remix-run/react";
import Nav from "~/components/Nav";
import BrowserNav from "~/components/BrowserNav";
import NoiseOverlay from "~/components/NoiseOverlay";
import ServiceWorkerRegister from "~/components/ServiceWorkerRegister";
import { useNonce } from "~/utils/nonce";
import globalStylesUrl from "~/styles/globals.css?url";

export const links: LinksFunction = () => [
  { rel: "preconnect", href: "https://fonts.googleapis.com" },
  {
    rel: "preconnect",
    href: "https://fonts.gstatic.com",
    crossOrigin: "anonymous",
  },
  {
    rel: "stylesheet",
    href: "https://fonts.googleapis.com/css2?family=Orbitron:wght@400;600;700;900&family=Inter:wght@400;600;700&family=Space+Grotesk:wght@400;600;700&family=Poppins:wght@400;600;700&family=Bebas+Neue&family=Rajdhani:wght@400;600;700&family=Exo+2:wght@400;600;700&family=JetBrains+Mono:wght@400;600;700&display=swap",
  },
  // Critical body font — discovered late in CSS otherwise
  {
    rel: "preload",
    href: "/fonts/IoskeleyMono-Regular.woff2",
    as: "font",
    type: "font/woff2",
    crossOrigin: "anonymous",
  },
  // External API used by Burn Rate
  { rel: "dns-prefetch", href: "https://open.er-api.com" },
  { rel: "preconnect", href: "https://open.er-api.com", crossOrigin: "anonymous" },
  { rel: "stylesheet", href: globalStylesUrl },
  { rel: "manifest", href: "/manifest.json" },
  { rel: "apple-touch-icon", href: "/apple-touch-icon.png" },
  { rel: "icon", type: "image/x-icon", href: "/favicon.ico" },
  { rel: "icon", type: "image/png", sizes: "32x32", href: "/favicon.png" },
];

export const meta: MetaFunction = () => [
  { title: "Prodigium Dynamics" },
  {
    name: "description",
    content:
      "An AI-driven studio building digital products, content systems, and connection layers.",
  },
  { property: "og:type", content: "website" },
  { property: "og:url", content: "https://prodigiumdynamics.space" },
  { property: "og:title", content: "Prodigium Dynamics" },
  {
    property: "og:description",
    content:
      "Bringing visions to life. AI systems, digital products, and personas engineered with precision.",
  },
  { name: "twitter:card", content: "summary_large_image" },
  { name: "twitter:title", content: "Prodigium Dynamics" },
  { name: "twitter:description", content: "Bringing visions to life." },
  { name: "theme-color", content: "#000000" },
  { name: "mobile-web-app-capable", content: "yes" },
  { name: "apple-mobile-web-app-capable", content: "yes" },
  { name: "apple-mobile-web-app-status-bar-style", content: "black-translucent" },
  { name: "apple-mobile-web-app-title", content: "Prodigium" },
];

export default function App() {
  const nonce = useNonce();
  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />
      </head>
      <body>
        <NoiseOverlay />
        <Nav />
        <BrowserNav />
        <Outlet />
        <ScrollRestoration nonce={nonce} />
        <Scripts nonce={nonce} />
        <ServiceWorkerRegister />
      </body>
    </html>
  );
}
